// SignupForm — F8.7b · email + password + strength meter + submit.
// On success shows the "verification email sent" panel with a resend button.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthModal } from "@/context/AuthModalContext";

function passwordStrength(pw) {
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score += 1;
    if (pw.length >= 12) score += 1;
    if (/[A-Z]/.test(pw)) score += 1;
    if (/[0-9]/.test(pw)) score += 1;
    if (/[^A-Za-z0-9]/.test(pw)) score += 1;
    return Math.min(score, 4);
}

export default function SignupForm({ prefillEmail = "" }) {
    const { t } = useTranslation("landing_v2");
    const { setTab } = useAuthModal();
    const [email, setEmail] = useState(prefillEmail);
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    // Resend verification state
    const [resendState, setResendState] = useState("idle"); // idle | sending | sent | error
    const [resendCooldown, setResendCooldown] = useState(0);
    const cooldownTimer = useRef(null);

    useEffect(() => () => { if (cooldownTimer.current) clearInterval(cooldownTimer.current); }, []);

    const startCooldown = (seconds = 60) => {
        setResendCooldown(seconds);
        if (cooldownTimer.current) clearInterval(cooldownTimer.current);
        cooldownTimer.current = setInterval(() => {
            setResendCooldown((s) => {
                if (s <= 1) { clearInterval(cooldownTimer.current); return 0; }
                return s - 1;
            });
        }, 1000);
    };

    const handleResend = async () => {
        if (resendState === "sending" || resendCooldown > 0) return;
        setResendState("sending");
        try {
            const res = await fetch(`/api/auth/resend-verification`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            if (res.ok) {
                setResendState("sent");
                startCooldown(60);
                setTimeout(() => setResendState("idle"), 4000);
            } else {
                setResendState("error");
                setTimeout(() => setResendState("idle"), 4000);
            }
        } catch {
            setResendState("error");
            setTimeout(() => setResendState("idle"), 4000);
        }
    };

    const strength = useMemo(() => passwordStrength(password), [password]);
    const strengthLabel = ["", "weak", "fair", "good", "strong"][strength] || "";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email || !password) return;
        if (password.length < 8) {
            setError(t("auth.modal.password_hint"));
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`/api/auth/register`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (data.detail === "email_already_registered") {
                    setError(t("auth.modal.error_email_already_registered"));
                } else if (typeof data.detail === "string" && data.detail.length < 100) {
                    setError(data.detail.replace(/_/g, " "));
                } else {
                    setError(t("auth.modal.error_generic"));
                }
                setSubmitting(false);
                return;
            }
            setSent(true);
        } catch {
            setError(t("auth.modal.error_generic"));
        }
        setSubmitting(false);
    };

    if (sent) {
        const resendLabel = resendState === "sending"
            ? "…"
            : resendState === "sent"
                ? t("auth.modal.resend_sent")
                : resendState === "error"
                    ? t("auth.modal.resend_error")
                    : resendCooldown > 0
                        ? t("auth.modal.resend_cooldown", { seconds: resendCooldown })
                        : t("auth.modal.resend_cta");
        const resendDisabled = resendState !== "idle" || resendCooldown > 0;
        return (
            <div className="text-center space-y-4 py-4" data-testid="auth-modal-signup-sent">
                <div className="w-12 h-12 mx-auto rounded-full bg-brand-success/15 border border-brand-success/40 flex items-center justify-center">
                    <svg className="w-6 h-6 text-brand-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="text-text-main text-lg font-semibold">
                    {t("auth.modal.verification_sent_title")}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                    {t("auth.modal.verification_sent_body", { email })}
                </p>
                <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendDisabled}
                    data-testid="auth-modal-resend-btn"
                    className="text-xs text-brand-primary hover:text-brand-secondary transition-colors disabled:text-text-secondary disabled:cursor-not-allowed"
                >
                    {resendLabel}
                </button>
                <div>
                    <button
                        type="button"
                        onClick={() => setTab("login")}
                        className="text-xs text-text-secondary hover:text-text-main transition-colors"
                        data-testid="auth-modal-back-to-login"
                    >
                        {t("auth.modal.toggle_to_login")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-modal-signup-form">
            <div>
                <label htmlFor="signup-email" className="block text-xs text-text-secondary mb-1.5">
                    {t("auth.modal.email_label")}
                </label>
                <input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.modal.email_placeholder")}
                    data-testid="auth-modal-signup-email"
                    className="w-full px-3 py-2.5 rounded-xl bg-bg border border-brand-border text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-brand-primary/60 transition-colors"
                />
            </div>
            <div>
                <label htmlFor="signup-password" className="block text-xs text-text-secondary mb-1.5">
                    {t("auth.modal.password_label")}
                </label>
                <input
                    id="signup-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth.modal.password_placeholder")}
                    data-testid="auth-modal-signup-password"
                    className="w-full px-3 py-2.5 rounded-xl bg-bg border border-brand-border text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-brand-primary/60 transition-colors"
                />
                {password && (
                    <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1 bg-brand-border rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all ${
                                    strength <= 1 ? "bg-brand-alert w-1/4"
                                    : strength === 2 ? "bg-neutral-500 w-2/4"
                                    : strength === 3 ? "bg-brand-secondary w-3/4"
                                    : "bg-brand-success w-full"
                                }`}
                            />
                        </div>
                        <span className="text-[10px] text-text-secondary uppercase tracking-wide">
                            {strengthLabel}
                        </span>
                    </div>
                )}
                <div className="text-[11px] text-text-secondary mt-1.5">
                    {t("auth.modal.password_hint")}
                </div>
            </div>
            {error && (
                <div className="text-xs text-brand-alert" data-testid="auth-modal-signup-error">
                    {error}
                </div>
            )}
            <button
                type="submit"
                disabled={submitting}
                data-testid="auth-modal-signup-submit"
                className="w-full px-4 py-2.5 rounded-xl bg-white hover:bg-[#EDEDED] text-black font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {submitting ? "…" : t("auth.modal.submit_signup")}
            </button>
            <div className="text-center text-xs text-text-secondary pt-1">
                <button
                    type="button"
                    onClick={() => setTab("login")}
                    className="hover:text-text-main transition-colors"
                    data-testid="auth-modal-toggle-login"
                >
                    {t("auth.modal.toggle_to_login")}
                </button>
            </div>
        </form>
    );
}
